import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import { activityMonthsPath, activityPath, type ActivityFilter, type ActivityMonths, type ActivityPage } from '../activity.ts';

/**
 * A client's Timeline (A-#157): GET /clients/:id/activity, newest first, a page at a time by its cursor. Read fresh on
 * every open (staleTime 0), so an action just recorded is there when the Timeline is opened next.
 */
export function useClientActivity(clientId: string, filter: ActivityFilter) {
  const { getToken } = useSession();
  return useInfiniteQuery({
    ...queryDefaults,
    staleTime: 0,
    queryKey: ['client', clientId, 'activity', filter.kind ?? '', filter.department ?? ''],
    queryFn: ({ pageParam }) => apiFetch<ActivityPage>(activityPath(clientId, filter, pageParam), getToken),
    initialPageParam: null as ActivityPage['next'],
    getNextPageParam: (last) => last.next ?? undefined,
  });
}

/** Deploy 8 (A-#227): how many entries each month holds, for these filters, read fresh as the Timeline is. */
export function useActivityMonths(clientId: string, filter: ActivityFilter) {
  const { getToken } = useSession();
  return useQuery({
    ...queryDefaults,
    staleTime: 0,
    queryKey: ['client', clientId, 'activity-months', filter.kind ?? '', filter.department ?? ''],
    queryFn: () => apiFetch<ActivityMonths>(activityMonthsPath(clientId, filter), getToken),
  });
}

/**
 * Deploy 8 (A-#239): the Timeline's first load, ONE page from the start of the oldest open month on, split by month on
 * the page; paged by the same cursor ("Show earlier").
 */
export function useSinceActivity(clientId: string, filter: ActivityFilter, since: string) {
  const { getToken } = useSession();
  return useInfiniteQuery({
    ...queryDefaults,
    staleTime: 0,
    queryKey: ['client', clientId, 'activity', filter.kind ?? '', filter.department ?? '', 'since', since],
    queryFn: ({ pageParam }) => apiFetch<ActivityPage>(activityPath(clientId, filter, pageParam, undefined, since), getToken),
    initialPageParam: null as ActivityPage['next'],
    getNextPageParam: (last) => last.next ?? undefined,
  });
}

/** Deploy 8: one month's entries, read only when the month is opened; paged within the month by the same cursor. */
export function useMonthActivity(clientId: string, filter: ActivityFilter, month: string) {
  const { getToken } = useSession();
  return useInfiniteQuery({
    ...queryDefaults,
    staleTime: 0,
    queryKey: ['client', clientId, 'activity', filter.kind ?? '', filter.department ?? '', month],
    queryFn: ({ pageParam }) => apiFetch<ActivityPage>(activityPath(clientId, filter, pageParam, month), getToken),
    initialPageParam: null as ActivityPage['next'],
    getNextPageParam: (last) => last.next ?? undefined,
  });
}
